import { ApiError, apiJson } from './http';

/**
 * The one till (ΩΞ-TILL-DIGITAL-001). The site's own checkout, on Stripe.
 *
 * POST /api/checkout/session.php — the server resolves the catalogue id against its own
 * catalogue file, prices it from that file (never from this request), opens a Stripe Checkout
 * Session and answers `{ success: true, url }`. An item the reader already owns answers 409
 * `{ owned: true, route }`: there is nothing to buy, so the reader is taken to it instead.
 */
export type TillSession =
  { readonly kind: 'checkout'; readonly url: string } | { readonly kind: 'owned'; readonly route: string };

export interface TillAttribute {
  readonly key: string;
  readonly value: string;
}

export const TILL_CHECKOUT_FAILED = 'Could not open checkout. Nothing was charged.';

/** Stripe-hosted checkout pages; anything else in `url` is refused rather than followed. */
/** Stripe's hosted page, or the site's own checkout page (ΩΞ-CHECKOUT-OWN-001) with the session's client secret in the fragment. */
const STRIPE_CHECKOUT = /^(https:\/\/checkout\.stripe\.com\/|https:\/\/mutatio618\.com\/checkout\/#cs=cs_(test|live)_)/;

export async function openTillSession(
  id: string,
  from: string,
  attribution: readonly TillAttribute[] = [],
): Promise<TillSession> {
  try {
    const body = await apiJson<{ success: true; url: string }>('/api/checkout/session.php', {
      method: 'POST',
      auth: true,
      body: { id, from, attribution, ui: 'own' },
      fallbackError: TILL_CHECKOUT_FAILED,
    });
    if (typeof body.url !== 'string' || !STRIPE_CHECKOUT.test(body.url)) throw new Error(TILL_CHECKOUT_FAILED);
    return { kind: 'checkout', url: body.url };
  } catch (error) {
    if (error instanceof ApiError && error.status === 409) {
      const route = (error.body as { owned?: unknown; route?: unknown } | null)?.route;
      if (typeof route === 'string' && route.startsWith('/')) return { kind: 'owned', route };
    }
    throw error;
  }
}

/**
 * Ω GEAR (ΩΞ-TILL-MERCH-001): the cart's lines — catalogue ids and counts, nothing else — and the
 * country the order ships to. The server prices every line from gear.json and the shipping from
 * Printful's own rate for that country, and restricts the Stripe address to it.
 */
export async function openTillCart(
  lines: readonly { readonly id: string; readonly qty: number }[],
  country: string,
  from: string,
  attribution: readonly TillAttribute[] = [],
): Promise<string> {
  const body = await apiJson<{ success: true; url: string }>('/api/checkout/session.php', {
    method: 'POST',
    auth: true,
    body: { lines, country, from, attribution, ui: 'own' },
    fallbackError: TILL_CHECKOUT_FAILED,
  });
  if (typeof body.url !== 'string' || !STRIPE_CHECKOUT.test(body.url)) throw new Error(TILL_CHECKOUT_FAILED);
  return body.url;
}

/** The till's catalogue: id → price in cents. GET /api/checkout/catalogue.php. */
export interface TillCatalogue {
  readonly items: Readonly<Record<string, { readonly amount: number; readonly available: boolean }>>;
}

export function fetchTillCatalogue(): Promise<TillCatalogue> {
  return apiJson<TillCatalogue>('/api/checkout/catalogue.php');
}
